package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.css.TransformOrigin
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.transformOrigin
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import dev.lingvo.site.ds.ControlHeight
import dev.lingvo.site.ds.Elevation
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.Transparent
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.elevation
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.StylePropertyValue
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

val EditorFieldContentStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .maxWidth(EDITOR_CONTENT_MAX_WIDTH_PX.px)
        .gap(Space.XXL)
        .padding(
            top = Space.XL,
        )
        .styleModifier {
            property("box-sizing", "border-box")
            property("margin", "0 auto")
        }
}

val EditorIdentityStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("align-items", "flex-start")
            property("justify-content", "space-between")
            property("flex-wrap", "wrap")
        }
}

val EditorIdentityActionsStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("flex-wrap", "wrap")
        }
}

val EditorIdentityMetadataStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        .gap(Space.XS)
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .styleModifier {
            property("display", "flex")
            property("align-items", "baseline")
            property("min-width", "0")
            property("overflow", "hidden")
            property("white-space", "nowrap")
        }
}

val EditorDetailsBodyStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .gap(Space.MD)
        .padding(top = Space.MD, bottom = Space.SM)
        .styleModifier {
            property("border-top", "1px solid ${colorMode.toSitePalette().border}")
        }
}

val EditorDetailsFieldStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .gap(Space.XS)
}

val EditorDetailsLabelStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Micro)
        .fontWeight(700)
        .styleModifier {
            property("letter-spacing", "0")
            property("text-transform", "uppercase")
        }
}

val EditorDetailsValueStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textPrimary)
        .fontSize(TypeScale.BodySmall)
        .lineHeight(1.5)
        .styleModifier { property("overflow-wrap", "anywhere") }
}

val EditorDetailsTagsStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
        }
}

val EditorPresenceStyle = CssStyle.base {
    Modifier
        .gap(Space.XS)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("flex-wrap", "wrap")
        }
}

val EditorPresenceCountStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Micro)
        .fontWeight(700)
}

val EditorEyebrowStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textMuted)
        .fontSize(TypeScale.Micro)
        .fontWeight(600)
        .styleModifier {
            property("letter-spacing", "0")
            property("text-transform", "uppercase")
        }
}

val EditorMetaStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .lineHeight(1.5)
}

val SourceSurfaceStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Control)
        .padding(Space.LG)
        .gap(Space.MD)
        .fontSize(TypeScale.Reading)
        .lineHeight(1.65)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("overflow-wrap", "anywhere")
            property("white-space", "pre-wrap")
            // The source used to grow without limit and could push the field it is meant to sit
            // above below the fold. Capped to the same height the target field allows itself, with
            // its own scrollbar rather than the page's.
            property("max-height", "40vh")
            property("overflow-y", "auto")
        }
}

val SourceTextStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .fontSize(TypeScale.Reading)
        .lineHeight(1.65)
        .styleModifier {
            property("overflow-wrap", "anywhere")
            property("white-space", "pre-wrap")
        }
}

val EditorTargetFieldStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .minWidth(0.px)
            .gap(Space.XS)
    }
}

val EditorTargetTextAreaStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .minWidth(0.px)
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().textMuted)
            .borderRadius(Radius.Control)
            .padding(Space.MD)
            .fontSize(TypeScale.Reading)
            .lineHeight(1.6)
            .styleModifier {
                property("box-sizing", "border-box")
                // The field the owner actually types into used to be pinned to a hard 120px — 2.8
                // visible lines at this size — while the read-only source above it grew without any
                // cap. Adopts LTextAreaStyle's own auto-grow contract instead: three lines at rest
                // (3 * 1.6 line-height + Space.MD padding top/bottom + the 1px border on each side),
                // growing with what is typed up to 40% of the viewport before it scrolls internally.
                property("field-sizing", "content")
                property("min-height", "calc(3 * 1.6em + 26px)")
                property("max-height", "40vh")
                property("overflow-y", "auto")
                // No `resize: vertical` any more. The field now grows on its own with what is
                // typed, so a manual grip is largely redundant - and it was never safe: the well
                // beneath the field is painted by EditorTargetMirrorStyle, which has no
                // ResizeObserver on the textarea, so a hand-dragged height (unconnected to content,
                // unconstrained by min/max-height) left the extra strip painted in the pane's
                // `surface` instead of `surfaceSunken`, with every QA underline past the mirror's
                // own height silently clipped. Content-driven growth keeps both layers in the one
                // min/max-height contract above instead.
                property("outline", "none")
            }
    }
    cssRule(":focus") {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * A finger, not a cursor. Read by every rule in this file that has to change shape for touch, so
 * the toolbar's hint text and the 44px targets cannot end up answering different questions.
 */
private val COARSE_POINTER = CSSMediaQuery.MediaFeature("pointer", StylePropertyValue("coarse"))

val EditorCoarseTargetStyle = CssStyle {
    cssRule(COARSE_POINTER) {
        Modifier.minWidth(44.px).minHeight(44.px)
    }
}

/**
 * The punctuation strip, and the sentence each key needs on a device with no hover.
 *
 * The hint used to live only in a `title` attribute. A tooltip is unreachable on touch and
 * unreachable from the keyboard, so on the phone this toolbar is explicitly sized for, the one
 * explanation of the narrow no-break space — the character the whole strip exists to rescue for
 * French — could not be read at all. The hint is now a real text node beside its control: clipped
 * at a fine pointer, where the tooltip does work and the sentence would be noise, and laid out as
 * visible text at a coarse one.
 *
 * Clipped, not `display: none` or `visibility: hidden` — those remove it from the accessibility
 * tree as well, which would trade a tooltip nobody can open for a sentence nobody can hear. Same
 * technique, and same reason, as `LVisuallyHiddenStyle`.
 */
val EditorFieldToolbarStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .gap(Space.XS)
            .position(Position.Relative)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("flex-wrap", "wrap")
            }
    }
    cssRule(" [data-editor-punctuation-item]") {
        Modifier
            .minWidth(0.px)
            .gap(Space.SM)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
            }
    }
    /**
     * A key, drawn as a key.
     *
     * The four punctuation buttons used to be `GHOST`, so at rest they were four bare marks on the
     * page — no ground, no edge — sitting between a red QA sentence and the budget meter, where
     * they read as rendering debris rather than as a toolbar. They are `SECONDARY` now (hairline
     * and surface, which is what makes a control look like a control); this squares them so the
     * low comma and the raised quotes sit in boxes of one size rather than each on its own optical
     * baseline. `min-width` rather than `width`: a two-character mark still has room to grow.
     */
    cssRule(" [data-editor-punctuation-key]") {
        Modifier
            .minWidth(ControlHeight.SM)
            .styleModifier {
                property("padding-left", "0")
                property("padding-right", "0")
                property("justify-content", "center")
                property("text-align", "center")
            }
    }
    cssRule(" [data-editor-punctuation-hint]") {
        Modifier.styleModifier {
            property("position", "absolute")
            property("width", "1px")
            property("height", "1px")
            property("margin", "-1px")
            property("padding", "0")
            property("overflow", "hidden")
            property("clip", "rect(0, 0, 0, 0)")
            property("white-space", "nowrap")
            property("border", "0")
        }
    }
    // One key per row once the hint is visible: the sentence is longer than the strip is wide, and
    // half a sentence beside a 44px target is worse than no sentence at all.
    cssRule(COARSE_POINTER, " [data-editor-punctuation-item]") {
        Modifier.styleModifier { property("flex", "1 1 100%") }
    }
    cssRule(COARSE_POINTER, " [data-editor-punctuation-hint]") {
        Modifier
            .minWidth(0.px)
            .color(colorMode.toSitePalette().textMuted)
            .fontSize(TypeScale.Small)
            .lineHeight(1.4)
            .styleModifier {
                property("position", "static")
                property("width", "auto")
                property("height", "auto")
                property("margin", "0")
                property("overflow", "visible")
                property("clip", "auto")
                property("white-space", "normal")
            }
    }
}

val EditorFieldToolbarMoreStyle = CssStyle.base {
    Modifier.position(Position.Static)
}

val EditorFieldToolbarPopoverStyle = CssStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .minWidth(160.px)
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Control)
            .padding(Space.XS)
            // The one floating surface in the writing column, on the same rung as every menu,
            // tooltip and toast. It used to hand-write `0 8px 24px -8px rgba(0,0,0,.35)`, which is
            // the dark-mode shadow drawn on both grounds — on paper it read as a smudge rather than
            // a lift, because a light surface needs a lighter shadow and this one never got one.
            .elevation(Elevation.Floating)
            .styleModifier {
                // `transform-origin` takes physical keywords and has no logical spelling, so the
                // inline edge comes from `--l-inline-end`, which the theme flips under `[dir=rtl]`.
                // The panel is anchored to that same edge below; an origin that disagreed with the
                // anchor would scale the popover out of the corner it is not attached to.
                property("transform-origin", "top var(--l-inline-end, right)")
                property("top", "calc(100% + 4px)")
                property("inset-inline-end", "0")
                property("max-width", "min(320px, calc(100vw - 16px))")
                property("box-sizing", "border-box")
                property("z-index", "30")
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("gap", Space.XS.toString())
                property(
                    "transition",
                    "opacity ${Motion.StandardMs}ms ${Motion.EaseEntrance}, " +
                        "transform ${Motion.StandardMs}ms ${Motion.EaseEntrance}",
                )
            }
    }
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property("transform", "translateY(-${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})")
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property("transform", "translateY(-${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})")
            property(
                "transition",
                "opacity ${Motion.FastMs}ms ${Motion.EaseIn}, transform ${Motion.FastMs}ms ${Motion.EaseIn}",
            )
            property("pointer-events", "none")
        }
    }
}

val EditorActionBarStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .backgroundColor(colorMode.toSitePalette().surface)
            .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("flex-shrink", "0")
            }
    }
    cssRule(" [data-editor-action-metadata]") {
        Modifier
            .minWidth(0.px)
            .styleModifier {
                property("display", "flex")
                property("flex", "1 1 auto")
                property("flex-direction", "column")
                property("min-width", "0")
                property("overflow", "hidden")
            }
    }
}

val EditorActionBarRowStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .height(EDITOR_ACTION_BAR_HEIGHT_PX.px)
        .minHeight(EDITOR_ACTION_BAR_HEIGHT_PX.px)
        .padding(leftRight = Space.MD)
        .gap(Space.MD)
        .styleModifier {
            property("box-sizing", "border-box")
            property("display", "flex")
            property("align-items", "center")
            property("overflow", "hidden")
        }
}

val EditorSaveStatusStyle = CssStyle.base {
    Modifier
        .color(colorMode.toSitePalette().textSecondary)
        .fontSize(TypeScale.Small)
        .styleModifier { property("white-space", "nowrap") }
}

/**
 * A refusal the reader can still reach, painted like every other refusal.
 *
 * The dock refuses Approve while a QA error stands, and it refuses it with `aria-disabled` rather
 * than `enabled = false` on purpose: the native attribute takes the button out of the tab order, so
 * a keyboard reader would never meet the control or the sentence saying why it cannot be pressed.
 * That choice is right and it left the button on the wrong side of a repaint. `LButton` drops a
 * filled variant to the neutral pair — `surfaceSunken` under `textMuted` inside `borderStrong` —
 * whenever it is *unavailable*, and cancels the 0.56 wash for exactly those buttons; but it keys
 * that off `enabled = false`, which is the one thing this button must not be. So the accent fill
 * stayed, the wash landed on top of it, and the refused primary measured about 2.8:1 — the same
 * defect the design system had already fixed everywhere it could reach.
 *
 * This is that repaint, said from the call site, with the same three tokens so the two cannot drift.
 * Only the paint is borrowed: `aria-disabled`, the `aria-describedby` pointing at the reason, the
 * `cursor: not-allowed` and the button's place in the tab order are all untouched.
 *
 * The rule carries `[data-editor-refusal="true"]` as well as `[aria-disabled="true"]` for one
 * reason: `LButtonStyle`'s own wash is `.l-button[aria-disabled="true"]`, and matching that
 * specificity would leave which one wins to stylesheet order. One more attribute is one more point
 * of specificity, and the answer stops depending on registration order.
 */
val EditorRefusedActionStyle = CssStyle {
    cssRule("[aria-disabled=\"true\"][data-editor-refusal=\"true\"]") {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textMuted)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .styleModifier {
                // Never both mechanisms at once: fading the neutral pair takes it back under 3:1,
                // which is the argument `LButtonStyle` makes for its own repainted buttons.
                property("opacity", "1")
                property("transform", "none")
            }
    }
}

val EditorQaBlockerStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .color(colorMode.toSitePalette().danger)
        .padding(topBottom = Space.SM, leftRight = Space.MD)
        .fontSize(TypeScale.Small)
        .lineHeight(1.4)
        // `SM`, the one distance the product puts between a glyph and the words it belongs to —
        // `LButton`'s leading icon, the utility pane's tab strip. At `XS` the danger circle abutted
        // the "A" of its own sentence, which is the same collision §2.8 names for the tabs.
        .gap(Space.SM)
        .styleModifier {
            property("box-sizing", "border-box")
            property("overflow-wrap", "anywhere")
            // It carries a glyph, the sentence and a counted "(1 of 3)" now, so it lays them out
            // rather than relying on the block flow a single text node needed.
            property("display", "flex")
            property("align-items", "center")
            property("flex-wrap", "wrap")
    }
}

val QaReasonStyle = CssStyle.base {
    Modifier
        .styleModifier {
            property("display", "flex")
            property("align-items", "flex-start")
            property("flex-wrap", "wrap")
        }
        .gap(Space.SM)
}

/**
 * The layer that carries QA underlines beneath the target textarea.
 *
 * **A `<textarea>` cannot hold inline marks**, which is why `docs/DESIGN.md`'s first QA layer — "the
 * offending fragment is underlined in the target field" — shipped as a reason chip and nothing
 * else. A translator with a wrong quote mark or a stray placeholder in a long string was told
 * *what* was wrong and had to find *where* themselves.
 *
 * So the text is drawn twice: once here, marked up and invisible, and once by the textarea on top
 * of it with a transparent background. The only thing this layer paints is the underline; its own
 * glyphs are transparent, so what the reader sees is always the real input's text.
 *
 * **Every metric that affects where a glyph lands must be identical in both layers**, or the
 * underline drifts from the word it belongs to — which is worse than not drawing it. That is why
 * the box, padding, border, type and wrapping below are duplicated from
 * [EditorTargetTextAreaStyle] rather than approximated, and why `EditorTargetFieldMirrorTest`
 * asserts the two wrap to the same height for Latin, CJK and RTL samples.
 */
val EditorTargetMirrorStyle = CssStyle.base {
    Modifier
        .position(Position.Absolute)
        .top(0.px)
        .left(0.px)
        .width(100.percent)
        // The field's fill lives here now: the textarea above is transparent so this layer's
        // underlines show through, and something has to paint the well.
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .border(1.px, LineStyle.Solid, Transparent)
        .borderRadius(Radius.Control)
        .padding(Space.MD)
        .fontSize(TypeScale.Reading)
        .lineHeight(1.6)
        .styleModifier {
            property("box-sizing", "border-box")
            // Tracks EditorTargetTextAreaStyle's own min/max-height exactly, with no fixed height
            // of its own, so this layer grows the same amount the field does. A mismatch here is
            // marks past line three silently clipped by `overflow: hidden` below — this used to be
            // locked to a flat 120/240 while the field itself could be dragged well past it.
            property("min-height", "calc(3 * 1.6em + 26px)")
            property("max-height", "40vh")
            // **The same overflow rule as the textarea above, and that is the whole fix.**
            //
            // This was `overflow: hidden`, so it never reserved a scrollbar while the field above
            // it — `overflow-y: auto` — took one the moment a translation grew past the box. The
            // two then wrapped at different columns and every QA underline sat about 15px right of
            // the character it marked, drifting further along the line. Invisible on macOS, whose
            // overlay scrollbars reserve nothing; visible to everyone on Windows or Linux.
            //
            // Matching the rule rather than measuring the gutter: both layers carry the same
            // min-height, max-height, font and content, so they overflow at the same moment and
            // reserve the same width without anything having to know how wide a scrollbar is. A
            // measured value in a CSS variable was tried first and was worse — it is unset until
            // JS runs, so the field reserved and the mirror did not, and every case mismatched
            // instead of one.
            //
            // Nothing scrolls this layer: it follows the field by transform, so its own scrollTop
            // stays 0 and the field's scrollbar is painted over the strip this reserves.
            property("overflow-x", "hidden")
            property("overflow-y", "auto")
            // The wrapping contract. `pre-wrap` keeps the runs of spaces a translator typed, and
            // `break-word` matches what a textarea does with a word longer than its line.
            property("white-space", "pre-wrap")
            property("overflow-wrap", "break-word")
            property("word-break", "normal")
            // Transparent glyphs: this layer exists for its decoration, not its text. The textarea
            // above paints the real characters.
            property("color", "transparent")
            property("pointer-events", "none")
            property("user-select", "none")
        }
}

/** The wrapper that lets the mirror sit exactly under the input. */
val EditorTargetMirrorHostStyle = CssStyle.base {
    Modifier
        .position(Position.Relative)
        .width(100.percent)
        .minWidth(0.px)
}

val QaUnderlineStyle = CssStyle.base {
    Modifier.styleModifier {
        property("text-decoration", "underline wavy")
        property("text-decoration-color", colorMode.toSitePalette().danger.toString())
        property("text-decoration-thickness", "1.5px")
        property("text-underline-offset", "3px")
    }
}

/** The same mark in the warning tone. DESIGN.md asks for danger *and* warning, not one of them. */
val QaUnderlineWarningStyle = CssStyle.base {
    Modifier.styleModifier {
        property("text-decoration", "underline wavy")
        property("text-decoration-color", colorMode.toSitePalette().warning.toString())
        property("text-decoration-thickness", "1.5px")
        property("text-underline-offset", "3px")
    }
}

